import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Slide,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { DataDilogLayout } from "../Objects/interfaces/AppInterface";
import useTailwindBrand from "../Theme/useTailwindBrand";
import ButtonField from "./Ui/ButtonField";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DialogLayout: React.FC<DataDilogLayout> = (data) => {
  const { brandConfig } = useTailwindBrand();
  const primaryColor = brandConfig.colors?.primary ?? "#0054A3";
  const closeColor = "#64748b";

  const showActions =
    data?.customDialogActions !== "none" && (data.cancel != null || data.valid != null || data?.customBtnValid);

  return (
    <Dialog
      open={data.open}
      onClose={data.handleClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth={data?.maxWidth ?? "sm"}
      fullWidth={data?.maxWidth !== "sm"}
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b border-gray-100"
        style={{
          backgroundColor: "white",
          fontFamily: brandConfig.fontFamily,
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            flex: 1,
            fontFamily: brandConfig.fontFamily,
            fontWeight: 700,
            color: primaryColor,
            fontSize: "1.125rem",
          }}
        >
          {data.title}
        </Typography>
        <IconButton
          onClick={data.handleCloseIcon ?? data.handleClose}
          aria-label="Fermer"
          size="small"
          sx={{
            color: closeColor,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
              color: "#334155",
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Content */}
      <DialogContent
        sx={{
          px: 3,
          py: 3,
          fontFamily: brandConfig.fontFamily,
        }}
      >
        {data.text != null && (
          <div className="text-gray-700 leading-relaxed">{data.text}</div>
        )}
        {data.children}
      </DialogContent>

      {/* Actions */}
      {showActions && (
        <DialogActions
          sx={{
            display: data?.customDialogActions === "none" ? "none" : "flex",
            gap: 1.5,
            flexWrap: "wrap",
            px: 3,
            py: 2.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "grey.50",
          }}
          className={data?.customDialogActions}
        >
          {data.cancel && (
            <ButtonField
              label={data.cancel}
              variant="outlined"
              className="min-w-[120px] font-semibold"
              onClick={data.handleClose}
            />
          )}
          {data?.customBtnValid ?? (
            data.valid && (
              <ButtonField
                label={data.valid}
                variant="contained"
                className="min-w-[120px] font-semibold"
                onClick={data.handleSubmit}
                disabled={data?.load}
                loading={data?.load}
              />
            )
          )}
        </DialogActions>
      )}
      {data?.btnChildren}
    </Dialog>
  );
};

export default DialogLayout;
